import React, { useState, useEffect, useRef, useMemo } from 'react';
import { productAPI, orderAPI, dealerAPI, analyticsAPI, exportAPI, transporterAPI, crmAPI, stockAPI, productionAPI, getFileUrl } from '../../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { LayoutDashboard, Package, ShoppingBag, Users, LogOut, Plus, Edit, Trash2, CheckCircle, Truck, Loader2, FileText, BarChart3, PackageCheck, DollarSign, Download, IndianRupee, Upload, ImageIcon, ArrowUpDown, Eye, CalendarClock, Pencil, ClipboardList, Undo2, Menu, X, UserRound, MessageCircle, ArrowLeft, Warehouse, Minus, Factory } from 'lucide-react';
import { DispatchRecords } from './DispatchRecords';
import { FreightSection } from './FreightSection';
import { useConfirm } from '@/components/ConfirmProvider';


export const TransportTab = ({ transporters = [], fetchData, userRole }) => {
  const confirm = useConfirm();
  const [subTab, setSubTab] = useState('records'); // default to records
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [name, setName] = useState('');
  const [vehicleDrafts, setVehicleDrafts] = useState({}); // { transporterId: "" } for inline add
  const [driverNameDrafts, setDriverNameDrafts] = useState({});
  const [driverPhoneDrafts, setDriverPhoneDrafts] = useState({});

  const openAdd = () => { setEditTarget(null); setName(''); setDialogOpen(true); };
  const submit = async () => {
    const v = name.trim();
    if (!v) { toast.error('Name is required'); return; }
    try {
      if (editTarget) {
        await transporterAPI.update(editTarget.id, { name: v, vehicles: editTarget.vehicles, drivers: editTarget.drivers });
        toast.success('Transporter updated');
      } else {
        await transporterAPI.create({ name: v, vehicles: [], drivers: [] });
        toast.success('Transporter added');
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };
  const handleDelete = async (t) => {
    if (!(await confirm({
      title: `Delete "${t.name}"?`,
      description: 'All its vehicles and drivers will be deleted too. This cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true,
    }))) return;
    try { await transporterAPI.remove(t.id); toast.success('Deleted'); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const addVehicle = async (t) => {
    const num = (vehicleDrafts[t.id] || '').trim();
    if (!num) return;
    try {
      await transporterAPI.addVehicle(t.id, { number: num });
      setVehicleDrafts((p) => ({ ...p, [t.id]: '' }));
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const removeVehicle = async (t, v) => {
    try { await transporterAPI.removeVehicle(t.id, v.id); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const addDriver = async (t) => {
    const dn = (driverNameDrafts[t.id] || '').trim();
    const dp = (driverPhoneDrafts[t.id] || '').trim();
    if (!dn || !dp) { toast.error('Driver name & phone required'); return; }
    try {
      await transporterAPI.addDriver(t.id, { name: dn, phone: dp });
      setDriverNameDrafts((p) => ({ ...p, [t.id]: '' }));
      setDriverPhoneDrafts((p) => ({ ...p, [t.id]: '' }));
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const removeDriver = async (t, d) => {
    try { await transporterAPI.removeDriver(t.id, d.id); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="transport-section">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 md:mb-6">
        <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#2B5339' }}>Transport</h2>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded border overflow-hidden" style={{ borderColor: '#2B5339' }} data-testid="transport-subtabs">
            <button
              onClick={() => setSubTab('transporters')}
              className="px-4 py-1.5 text-sm font-medium"
              style={{ background: subTab === 'transporters' ? '#2B5339' : 'white', color: subTab === 'transporters' ? 'white' : '#2B5339' }}
              data-testid="transport-subtab-transporters"
            >Transporters</button>
            <button
              onClick={() => setSubTab('records')}
              className="px-4 py-1.5 text-sm font-medium border-l"
              style={{ background: subTab === 'records' ? '#2B5339' : 'white', color: subTab === 'records' ? 'white' : '#2B5339', borderColor: '#2B5339' }}
              data-testid="transport-subtab-records"
            >Dispatch Records</button>
            <button
              onClick={() => setSubTab('freight')}
              className="px-4 py-1.5 text-sm font-medium border-l"
              style={{ background: subTab === 'freight' ? '#2B5339' : 'white', color: subTab === 'freight' ? 'white' : '#2B5339', borderColor: '#2B5339' }}
              data-testid="transport-subtab-freight"
            >Freight</button>
          </div>
          {subTab === 'transporters' && (
            <Button onClick={openAdd} style={{ background: '#2B5339' }} data-testid="add-transporter-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Transporter
            </Button>
          )}
        </div>
      </div>

      {subTab === 'records' ? (
        <DispatchRecords transporters={transporters} />
      ) : subTab === 'freight' ? (
        <FreightSection transporters={transporters} userRole={userRole} />
      ) : transporters.length === 0 ? (
        <Card className="p-12 text-center" style={{ borderColor: '#E2DFD8' }}>
          <Truck className="w-12 h-12 mx-auto mb-3" style={{ color: '#E2DFD8' }} />
          <p style={{ color: '#5C6B61' }}>No transporters yet. Add one to manage vehicles & drivers.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {transporters.map((t) => (
            <Card key={t.id} className="p-4 md:p-5 bg-white border" style={{ borderColor: '#E2DFD8' }} data-testid="transporter-card">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-3 border-b" style={{ borderColor: '#E2DFD8' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded flex items-center justify-center" style={{ background: '#2B5339' }}>
                    <Truck className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-lg font-bold" style={{ color: '#1E2420' }}>{t.name}</p>
                    <p className="text-xs" style={{ color: '#5C6B61' }}>{(t.vehicles || []).length} vehicle{(t.vehicles || []).length !== 1 ? 's' : ''} · {(t.drivers || []).length} driver{(t.drivers || []).length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setEditTarget(t); setName(t.name); setDialogOpen(true); }} data-testid="edit-transporter-btn">
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(t)} data-testid="delete-transporter-btn">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Vehicles */}
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#2B5339' }}>Vehicles</p>
                  <div className="space-y-1.5 mb-2">
                    {(t.vehicles || []).length === 0 && <p className="text-xs" style={{ color: '#5C6B61' }}>No vehicles yet</p>}
                    {(t.vehicles || []).map((v) => (
                      <div key={v.id} className="flex items-center justify-between p-2 rounded text-sm" style={{ background: '#FBF9F6' }} data-testid="transporter-vehicle">
                        <span className="font-mono">{v.number}</span>
                        <button onClick={() => removeVehicle(t, v)} className="text-xs px-1.5 py-0.5 rounded hover:bg-red-50" style={{ color: '#C05C35' }} data-testid="remove-vehicle-btn">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Vehicle number"
                      value={vehicleDrafts[t.id] || ''}
                      onChange={(e) => setVehicleDrafts((p) => ({ ...p, [t.id]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') addVehicle(t); }}
                      className="flex-1 text-sm"
                      data-testid="new-vehicle-input"
                    />
                    <Button size="sm" onClick={() => addVehicle(t)} style={{ background: '#2B5339' }} data-testid="add-vehicle-btn">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Drivers */}
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#C05C35' }}>Drivers</p>
                  <div className="space-y-1.5 mb-2">
                    {(t.drivers || []).length === 0 && <p className="text-xs" style={{ color: '#5C6B61' }}>No drivers yet</p>}
                    {(t.drivers || []).map((d) => (
                      <div key={d.id} className="flex items-center justify-between p-2 rounded text-sm" style={{ background: '#FBF9F6' }} data-testid="transporter-driver">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{d.name}</p>
                          <p className="text-xs" style={{ color: '#5C6B61' }}>{d.phone}</p>
                        </div>
                        <button onClick={() => removeDriver(t, d)} className="text-xs px-1.5 py-0.5 rounded hover:bg-red-50" style={{ color: '#C05C35' }} data-testid="remove-driver-btn">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-1.5">
                    <Input
                      placeholder="Driver name"
                      value={driverNameDrafts[t.id] || ''}
                      onChange={(e) => setDriverNameDrafts((p) => ({ ...p, [t.id]: e.target.value }))}
                      className="flex-1 text-sm"
                      data-testid="new-driver-name-input"
                    />
                    <Input
                      placeholder="Phone"
                      value={driverPhoneDrafts[t.id] || ''}
                      onChange={(e) => setDriverPhoneDrafts((p) => ({ ...p, [t.id]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') addDriver(t); }}
                      className="w-28 text-sm"
                      data-testid="new-driver-phone-input"
                    />
                    <Button size="sm" onClick={() => addDriver(t)} style={{ background: '#C05C35' }} data-testid="add-driver-btn">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="transporter-dialog">
          <DialogHeader>
            <DialogTitle>{editTarget ? 'Edit Transporter' : 'Add Transporter'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Transporter Name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Rajasthan Freight Co."
                data-testid="transporter-name-input"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={submit} style={{ background: '#2B5339' }} data-testid="submit-transporter-btn">
              {editTarget ? 'Save' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

